import { doc, onSnapshot } from 'firebase/firestore'
import {
  bridgePatientProjectionSchema,
  type BridgePolicyId,
  type CustomerId,
} from '@gh/common/models'
import { firestore } from '@/firebase'

const DEFAULT_POLICY_BINDING_TIMEOUT_MS = 30_000

/**
 * Wait until `customers/{customerId}/projections/bridgePatient.policyId` matches
 * the selected policy — callable success only means L3.5 binding was written.
 */
export async function waitForBridgePatientPolicyId(
  customerId: CustomerId,
  expectedPolicyId: BridgePolicyId,
  options?: { timeoutMs?: number; signal?: AbortSignal },
): Promise<void> {
  if (options?.signal?.aborted) {
    throw new DOMException('Bridge policy create cancelled', 'AbortError')
  }
  const timeoutMs = options?.timeoutMs ?? DEFAULT_POLICY_BINDING_TIMEOUT_MS
  const signal = options?.signal
  const ref = doc(firestore, 'customers', customerId, 'projections', 'bridgePatient')

  return new Promise((resolve, reject) => {
    let settled = false

    let timeout: ReturnType<typeof setTimeout> | undefined
    // eslint-disable-next-line prefer-const -- assigned after `finish`; deferred unsubscribe needs the binding first
    let syncUnsubscribe: (() => void) | undefined

    const finish = (settle: () => void) => {
      if (settled) return
      settled = true
      if (timeout !== undefined) {
        clearTimeout(timeout)
      }
      signal?.removeEventListener('abort', onAbort)
      if (syncUnsubscribe) {
        syncUnsubscribe()
      } else {
        void Promise.resolve().then(() => syncUnsubscribe?.())
      }
      settle()
    }

    const onAbort = () => {
      finish(() => {
        reject(new DOMException('Bridge policy create cancelled', 'AbortError'))
      })
    }

    syncUnsubscribe = onSnapshot(
      ref,
      (snapshot) => {
        if (settled || !snapshot.exists()) {
          return
        }

        try {
          const projection = bridgePatientProjectionSchema.parse({
            id: snapshot.id,
            ...snapshot.data(),
          })
          if (projection.policyId === expectedPolicyId && projection.inSync === true) {
            finish(resolve)
          }
        } catch (error) {
          finish(() => {
            reject(error instanceof Error ? error : new Error(String(error)))
          })
        }
      },
      (error) => {
        finish(() => {
          reject(error)
        })
      },
    )

    if (!settled) {
      timeout = setTimeout(() => {
        finish(() => {
          reject(
            new Error(
              'Insurance policy binding is still in progress. Please wait a moment and try again, or continue as self-pay.',
            ),
          )
        })
      }, timeoutMs)

      signal?.addEventListener('abort', onAbort)
      if (signal?.aborted) {
        onAbort()
      }
    }
  })
}
